/**
 * 客户任务管理
 * @author tylerzzheng
 */
import { useEffect, useCallback, useState, useMemo } from 'react'
import { TaskListDetail, TaskStatusEnum } from '@types'
import { updateLeads } from '@services/lead'
import { message } from 'antd'
import { isArray, isEqualArray } from '@library'
import { useTableService } from '@hooks/useTableService'
import { getTaskListByCidWithCheck } from '@services/task'

export function useTask (cid: string) {
  const {
    loading,
    list,
    total,
    reload,
    setFilter,
  } = useTableService(getTaskListByCidWithCheck, { cid }, '获取客户任务失败')

  // 当customerid变化的时候重置type,和filter
  useEffect(() => {
    setFilter({ cid })
  }, [cid, setFilter])

  const [listUnhandled, listHandled, listClosed] = useMemo(() => {
    const listUnhandled = list?.filter?.(item => item.status === TaskStatusEnum.UNHANDLED) ?? []
    const listHandled = list?.filter?.(item => item.status === TaskStatusEnum.HANDLED) ?? []
    const listClosed = list?.filter?.(item => item.status === TaskStatusEnum.CLOSED) ?? []
    return [listUnhandled, listHandled, listClosed]
  }, [list])
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([])

  // 默认勾选以派发任务(当翻页等任何导致重新获取任务动作发生时)
  useEffect(() => {
    setSelectedTaskIds(listUnhandled.map(item => item.taskId))
  }, [listUnhandled, setSelectedTaskIds])

  /* 任务打标 */
  const updateLeadTag = useCallback(async (task: TaskListDetail, tags: number[], type: 'public' | 'private') => {
    const { leadInfo } = task
    if (!isArray(tags)) return
    // 如果tags相比原来没有变化，则不提交
    if (isEqualArray(tags, leadInfo?.[`${type}Tags`]?.map?.(tag => tag[`${type}TagId`]))) {
      message.warn('标签未改变')
      return
    }

    const [, err] = await updateLeads({
      filter: { leadIds: [leadInfo?.leadId] },
      updateResult: {
        [type === 'private' ? 'updateLeadPrivateTags' : 'updateLeadPublicTags']: {
          [`${type}TagIds`]: tags,
        },
      },
    })
    if (err) {
      message.error(`修改失败(${err.message})`)
      return
    }
    message.success('修改成功')
    reload()
  }, [reload])

  return [
    loading,
    total,
    listUnhandled,
    listHandled,
    listClosed,
    /* 任务分类 */
    selectedTaskIds, // 已勾选的任务ids
    setSelectedTaskIds, // 设置勾选的任务
    updateLeadTag,
    reload,
  ] as const
}
